// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Fetch layer for the Бриф promotion wiring. Two flavours:
//
//   • publisherCall<T>  — STRICT. Uses publisherFetch (bearer + JWT pre-check;
//                         401 → global logout). Throws on !ok. Use for every
//                         WRITE / chat action, where real errors must surface.
//
//   • softCall<T>       — SOFT. Adds the bearer; an explicit 401 clears the stale
//                         admin session, while other failures resolve to the
//                         supplied fallback with live:false.
//
// Worker rewrites `/api/publisher` → `/api/promotion`, so all `/api/publisher/*`
// paths are passed verbatim. connections / buffer / oauth / telegram / openai /
// app-store-connect paths are also verbatim (not rewritten). There is NO response
// envelope: the worker serialises the object directly; errors are `{ error }`.

import { useAuthStore } from '@/stores/authStore';
import { publisherFetch } from '@/services/publisherApi';

/**
 * Strict call for write/chat actions. Injects the bearer + JWT pre-check via
 * publisherFetch (so a 401 triggers a global logout), then parses JSON. On a
 * non-2xx response it throws `Error(json.error ?? 'HTTP <status>')`.
 */
export async function publisherCall<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await publisherFetch(path, init);
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (!res.ok) {
    const message =
      json && typeof json === 'object' && typeof (json as { error?: unknown }).error === 'string'
        ? (json as { error: string }).error
        : `HTTP ${res.status}`;
    throw new Error(message);
  }

  return json as T;
}

/** Result wrapper for soft reads: `live:false` flags a fallback/demo payload. */
export interface Loaded<T> {
  data: T;
  live: boolean;
  error?: string;
}

/**
 * Soft fetch for read-only operational screens. Adds the bearer from
 * useAuthStore. A 401 means the persisted admin JWT is no longer accepted, so
 * it clears that session and lets ProtectedRoute return to login. Network and
 * other failures remain non-destructive fallbacks.
 */
export async function softCall<T>(
  path: string,
  fallback: T,
  map: (json: unknown) => T,
  init?: RequestInit,
): Promise<Loaded<T>> {
  try {
    const token = useAuthStore.getState().token;
    const headers = new Headers(init?.headers);
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const res = await fetch(path, { ...init, headers });
    if (!res.ok) {
      let error = `HTTP ${res.status}`;
      try {
        const j = (await res.json()) as { error?: unknown };
        if (typeof j.error === 'string') error = j.error;
      } catch {
        /* keep the HTTP-status error */
      }
      if (res.status === 401) {
        useAuthStore.getState().logout();
        error = 'Сессия истекла. Войдите снова.';
      }
      return { data: fallback, live: false, error };
    }

    const json = (await res.json()) as unknown;
    return { data: map(json), live: true };
  } catch (e) {
    return { data: fallback, live: false, error: e instanceof Error ? e.message : 'offline' };
  }
}
